import React from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useInventory, useAnimals } from "@/lib/repo";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, ChevronRight, Download } from "lucide-react";
import { formatDateTime } from "@/lib/utils";
import { AnimalStatusChip } from "@/components/AnimalStatusChip";
import { exportUrl } from "@/lib/api";
import { getMeta, META } from "@/lib/db";
import { toast } from "sonner";

export default function InventoryResult() {
  const { id } = useParams();
  const navigate = useNavigate();
  const inv = useInventory(id);
  const loading = inv === undefined;
  const allAnimals = useAnimals({ farm_id: inv?.farm_id, lot_id: inv?.lot_id || undefined });
  const expectedSet = new Set(inv?.expected_animal_ids || []);
  const animals = allAnimals.filter((a) => expectedSet.has(a.id));

  if (loading) return <Skeleton className="h-64 w-full rounded-2xl" />;
  if (!inv) return null;
  const scannedSet = new Set(inv.scanned_animal_ids || []);
  const missingSet = new Set(inv.missing_animal_ids || []);
  const secondarySet = new Set(inv.scans_via_secondary || []);
  const animalsWithSecondary = animals.filter((a) => secondarySet.has(a.id));

  // Fix M (2026-09-21): animales escaneados que NO estaban en la lista de esperados
  // (altas hechas mientras el conteo seguía abierto). Contaban como presentes pero
  // no se veían en ninguna fila: el resumen decía 625 y la lista mostraba 621.
  const altasDuranteConteo = allAnimals.filter(
    (a) => scannedSet.has(a.id) && !expectedSet.has(a.id)
  );

  // Fix L (2026-09-21): el conteo se hace en el campo, muchas veces sin señal.
  // Este CSV se arma con lo que ya está en el dispositivo, así que funciona offline.
  // El Excel completo (3 hojas) sale del servidor y necesita conexión.
  const descargarCSVLocal = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const estados = { active: "Activo", dead: "Muerto", sold: "Vendido", slaughtered: "Sacrificado" };
    // Fix M (2026-09-21): se suman las altas hechas durante el conteo, que antes
    // quedaban fuera del archivo aunque contaran como presentes.
    const filas = [...animals, ...altasDuranteConteo].map((a) => [
      !expectedSet.has(a.id) ? "Presente (alta durante el conteo)"
        : scannedSet.has(a.id) ? "Presente" : missingSet.has(a.id) ? "Faltante" : "Pendiente",
      a.chip_code || "",
      a.lomo_number || "",
      a.category || "",
      a.sex === "female" ? "Hembra" : a.sex === "male" ? "Macho" : "",
      a.breed || "",
      estados[a.status] || a.status || "",
      secondarySet.has(a.id) ? "Si" : "",
    ]);
    const enc = ["Resultado", "Chip", "Lomo", "Categoria", "Sexo", "Raza", "Estado actual del animal", "Leido por chip secundario"];
    let csv = [enc, ...filas].map((f) => f.map(esc).join(",")).join("\n");
    if ((inv.unknown_chips || []).length) {
      csv += "\n\n" + esc("Chips leidos que no estan en el sistema") + "\n" + inv.unknown_chips.map((c) => esc(c)).join("\n");
    }
    csv += `\n\n${esc("Esperados al iniciar el conteo")},${esc(animals.length)}`
      + `\n${esc("Presentes")},${esc(scannedSet.size)}`
      + `\n${esc("   - de la lista de esperados")},${esc(animals.filter((a) => scannedSet.has(a.id)).length)}`
      + `\n${esc("   - dados de alta durante el conteo")},${esc(altasDuranteConteo.length)}`
      + `\n${esc("Faltantes")},${esc(missingSet.size)}`;
    const url = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `conteo-${(inv.name || "inventario").replace(/[^\w-]/g, "-")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // El Excel de 3 hojas (Faltantes / Detalle / Resumen) lo arma el servidor,
  // que es el único que puede resolver los UUIDs y la hora de cada lectura.
  const descargarExcelServidor = async () => {
    try {
      const token = await getMeta(META.AUTH_TOKEN);
      const res = await fetch(exportUrl(`inventories/${id}`, "xlsx"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(res.status);
      const cd = res.headers.get("Content-Disposition") || "";
      const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = match ? decodeURIComponent(match[1]) : "conteo.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Necesitás conexión para el Excel completo. Probá con el CSV, que funciona sin señal.");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={()=>navigate("/inventario")} className="h-9 px-2"><ArrowLeft size={18} /></Button>
        <div className="flex-1">
          <h1 className="font-heading text-2xl font-semibold">{inv.name || "Inventario"}</h1>
          <div className="text-xs text-muted-foreground">{formatDateTime(inv.started_at)} → {formatDateTime(inv.finished_at)} · {inv.status === "finished" ? "Cerrado" : inv.status === "cancelled" ? "Cancelado" : "En progreso"}</div>
        </div>
        {inv.status === "in_progress" && (
          <Button onClick={()=>navigate(`/inventario/${id}/sesion`)} className="bg-emerald-700 hover:bg-emerald-800">Continuar</Button>
        )}
      </div>

      {/* Fix L (2026-09-21): antes no había forma de sacar el resultado del conteo. */}
      <div className="flex gap-2">
        <Button
          variant="outline"
          className="flex-1 h-11"
          data-testid="inventory-export-xlsx"
          onClick={() => descargarExcelServidor()}
        >
          <Download size={16} className="mr-2" /> Excel completo
        </Button>
        <Button
          variant="outline"
          className="flex-1 h-11"
          data-testid="inventory-export-csv-local"
          onClick={descargarCSVLocal}
        >
          <Download size={16} className="mr-2" /> CSV (sin señal)
        </Button>
      </div>

      <div data-testid="inventory-summary" className="grid grid-cols-3 gap-3">
        <Summary label="Presentes" value={scannedSet.size} tone="blue" />
        <Summary label="Faltantes" value={missingSet.size} tone="red" />
        <Summary label="Desconocidos" value={(inv.unknown_chips || []).length} tone="yellow" />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Animales</CardTitle></CardHeader>
        <CardContent className="p-0 divide-y divide-border">
          {animals.map((a) => {
            const present = scannedSet.has(a.id);
            const missing = missingSet.has(a.id);
            const viaSecondary = secondarySet.has(a.id);
            return (
              <Link to={`/animales/${a.id}`} key={a.id} className="p-3 flex items-center justify-between" data-testid="inventory-result-row">
                <div className="min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2">
                    Lomo {a.lomo_number || "—"}
                    {viaSecondary && (
                      <span
                        data-testid={`inventory-result-secondary-${a.id}`}
                        className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-[hsl(var(--brand-warm)/0.16)] text-[hsl(var(--brand-warm))]"
                      >
                        Chip 2°
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">{a.chip_code}</div>
                </div>
                <AnimalStatusChip status={present ? "presente" : missing ? "faltante" : "pendiente"} />
              </Link>
            );
          })}
        </CardContent>
      </Card>

      {altasDuranteConteo.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Dados de alta durante el conteo</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Animales que se cargaron mientras el conteo estaba abierto y se escanearon en la misma sesión. Cuentan como presentes, pero no estaban en la lista inicial.
            </p>
          </CardHeader>
          <CardContent className="p-0 divide-y divide-border">
            {altasDuranteConteo.map((a) => (
              <Link
                to={`/animales/${a.id}`}
                key={a.id}
                className="p-3 flex items-center justify-between"
                data-testid="inventory-result-late-row"
              >
                <div>
                  <div className="text-sm font-medium">Lomo {a.lomo_number || "—"}</div>
                  <div className="text-xs text-muted-foreground font-mono">{a.chip_code}</div>
                </div>
                <ChevronRight size={16} className="text-muted-foreground" />
              </Link>
            ))}
          </CardContent>
        </Card>
      )}

      {animalsWithSecondary.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Leídos por chip secundario</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Animales que se identificaron con un chip de respaldo o reemplazo. Revisá físicamente y considerá actualizar el chip principal.
            </p>
          </CardHeader>
          <CardContent className="p-0 divide-y divide-border">
            {animalsWithSecondary.map((a) => (
              <Link
                to={`/animales/${a.id}`}
                key={a.id}
                className="p-3 flex items-center justify-between"
                data-testid="inventory-result-secondary-row"
              >
                <div>
                  <div className="text-sm font-medium">Lomo {a.lomo_number || "—"}</div>
                  <div className="text-xs text-muted-foreground font-mono">{a.chip_code}</div>
                </div>
                <ChevronRight size={16} className="text-muted-foreground" />
              </Link>
            ))}
          </CardContent>
        </Card>
      )}

      {(inv.unknown_chips || []).length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Chips desconocidos</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {inv.unknown_chips.map((c) => (
              <span key={c} className="font-mono text-xs px-2.5 py-1 rounded-full bg-[#FFF6D6] border border-[#D6A400] text-[#6B4E00]">{c}</span>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Summary({ label, value, tone }) {
  const tones = {
    blue: "bg-[#E8F1FF] text-[#123A8A] border-[#2F6FED]",
    red: "bg-[#FFE9E9] text-[#7A1B14] border-[#D92D20]",
    yellow: "bg-[#FFF6D6] text-[#6B4E00] border-[#D6A400]",
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
      <div className="font-heading text-3xl font-semibold leading-none mt-1">{value}</div>
    </div>
  );
}
